/**
 * Detection and identification — the "resolving out of haze" system (plan E6).
 *
 * A contact climbs a ladder: undetected → radar blip → visual silhouette → identified.
 * Each rung unlocks more HUD information, which is what makes fog and night genuinely
 * change how the game plays rather than just how it looks. The player never gets a
 * type label for something they cannot actually see.
 */

import type { Vessel, DetectionLevel } from './types';
import type { WeatherState } from './weather';
import { visibilityFactor } from './weather';
import { vesselSpec } from '../content/vessels';
import { RADAR_RANGE, RADAR_RANGE_UPGRADED } from './constants';
import { rangeToRig } from './ships';

export interface DetectionOptions {
  hasLongRadar: boolean;
  hasThermal: boolean;
  /** Spotlight aim bearing in radians, or null when the light is off. */
  spotlightBearing: number | null;
  /** The hydrophone upgrade: the only sensor that hears a submerged hull. */
  hasHydrophone: boolean;
}

const SPOTLIGHT_CONE = 0.28;
const SPOTLIGHT_RANGE = 1400;

/**
 * How far the hydrophone hears a running submarine, metres.
 *
 * Comfortably beyond the projector's reach, and deliberately so: the sensor's job is to
 * tell you something is out there and roughly where, long before you can do anything
 * about it. The dread is in the waiting.
 */
const HYDROPHONE_RANGE = 2600;

/**
 * Update one vessel's detection level. Returns the previous level so the caller can fire
 * a ContactNew or ContactIdentified event exactly on the tick something changes.
 */
export function updateDetection(
  v: Vessel,
  weather: WeatherState,
  dt: number,
  opts: DetectionOptions,
): DetectionLevel {
  const previous = v.detection;
  const spec = vesselSpec(v.kind);
  const range = rangeToRig(v);

  /*
   * Radar: range-limited but weather-independent. A submerged hull returns nothing —
   * which is the whole problem a submarine poses, and why the hydrophone exists.
   */
  const radarRange = opts.hasLongRadar ? RADAR_RANGE_UPGRADED : RADAR_RANGE;
  const onRadar = !v.submerged && range <= radarRange;

  /*
   * Hydrophone: hears a submerged boat by its screws, and hears nothing else useful.
   *
   * It is a bearing-and-range fix, not a picture — the contact resolves to 'radar' at
   * best, never to an identified silhouette, because you are listening to it. Its range
   * beats the eye by a wide margin but it only works on things that are actually under,
   * so it is a purpose-built answer to the one threat the other sensors cannot see.
   *
   * Without the upgrade a submerged boat is genuinely invisible between periscope
   * exposures, and finding her is dead reckoning off her last known position.
   */
  const onHydrophone = opts.hasHydrophone
    && v.submerged
    && v.speed > 0.5
    && range <= HYDROPHONE_RANGE;

  // Visual: the vessel's own size scaled by how far you can see today.
  const visibility = visibilityFactor(weather);
  let visualRange = spec.silhouetteRange * (opts.hasThermal ? Math.max(visibility, 0.75) : visibility);

  // A spotlight buys back close-range visibility at night, inside its cone.
  if (opts.spotlightBearing !== null && range < SPOTLIGHT_RANGE) {
    const bearing = Math.atan2(v.pos.x, v.pos.z);
    let delta = Math.abs(bearing - opts.spotlightBearing) % (Math.PI * 2);
    if (delta > Math.PI) delta = Math.PI * 2 - delta;
    if (delta < SPOTLIGHT_CONE) visualRange = Math.max(visualRange, SPOTLIGHT_RANGE);
  }

  // A submerged hull cannot be seen at all, whatever the weather or the spotlight.
  const visible = !v.submerged && range <= visualRange;

  /*
   * Track age and last-known position: the state the uncertainty ellipse is drawn from.
   *
   * Held whenever any sensor has the contact, and left to grow the moment they all lose
   * it. That growing number is the mini-sub encounter's core information problem — the
   * player is not hunting a marker, they are hunting a region that widens while they
   * think about it.
   */
  if (onRadar || visible || onHydrophone) {
    v.trackAge = 0;
    v.lastKnownX = v.pos.x;
    v.lastKnownZ = v.pos.z;
  } else {
    v.trackAge += dt;
  }

  if (onHydrophone && !onRadar && !visible) {
    // Heard but not seen: a bearing on the scope, never an identification.
    v.detection = 'radar';
    v.detectedFor = 0;
    return previous;
  }

  if (!onRadar && !visible) {
    v.detection = 'undetected';
    v.detectedFor = 0;
    return previous;
  }

  if (!visible) {
    v.detection = 'radar';
    v.detectedFor = 0;
    return previous;
  }

  // Visible: hold the silhouette steady for a moment before it resolves to a type.
  v.detectedFor += dt;
  const identifyAt = spec.identifyTime;
  v.detection = v.detectedFor >= identifyAt ? 'identified' : 'silhouette';
  return previous;
}

/** What the HUD is allowed to call this contact. */
export function contactLabel(v: Vessel): string {
  switch (v.detection) {
    case 'identified': return vesselSpec(v.kind).label;
    case 'silhouette': return 'Unidentified';
    case 'radar': return 'Radar contact';
    default: return '';
  }
}

/** Whether the contact should be drawn as a hostile marker. Radar alone does not tell. */
export function knownHostile(v: Vessel): boolean {
  return v.detection === 'identified' && v.faction === 'hostile';
}
